import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Building2, LogOut, Plus, LogIn, Pause, Play, Store, CalendarCheck, Search,
} from "lucide-react";

const TYPES = [
  ["ristorante", "Ristorante"], ["pizzeria", "Pizzeria"], ["esperienza", "Esperienza"],
  ["bb", "B&B"], ["agriturismo", "Agriturismo"], ["hotel", "Hotel"],
];

function Stat({ icon: Icon, label, value, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700", green: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700", primary: "bg-primary/10 text-primary",
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3">
      <div className={`grid h-11 w-11 place-items-center rounded-xl ${tones[tone]}`}><Icon className="h-5 w-5" /></div>
      <div>
        <p className="text-2xl font-bold text-slate-900 leading-none">{value}</p>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout, startImpersonation } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, b] = await Promise.all([api.get("/admin/stats"), api.get("/admin/businesses")]);
      setStats(s.data); setRows(b.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleStatus = async (b) => {
    const next = b.status === "suspended" ? "active" : "suspended";
    try {
      await api.patch(`/admin/businesses/${b.id}/status`, { status: next });
      toast.success(next === "suspended" ? `${b.name} sospesa` : `${b.name} riattivata`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const enterAs = async (b) => {
    try {
      await startImpersonation(b.id, b.name);
      toast.success(`Sei entrato come ${b.name}`);
      nav("/app");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doLogout = () => { logout(); nav("/login"); };

  const filtered = rows.filter((r) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [r.name, r.owner_email, r.type].filter(Boolean).some((x) => x.toLowerCase().includes(s));
  });

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return "—"; }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="grid h-9 w-9 place-items-center rounded-xl bg-slate-900 text-white"><Building2 className="h-5 w-5" /></div>
          <div className="leading-tight">
            <p className="font-heading font-bold text-slate-900 text-sm sm:text-base">Amministrazione piattaforma</p>
            <p className="text-[11px] text-primary font-medium">turismo.latina.it — gestione attività</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden sm:block text-xs text-slate-500">{user?.email}</span>
          <Button variant="ghost" size="sm" className="text-slate-600" onClick={doLogout} data-testid="admin-logout">
            <LogOut className="h-4 w-4 mr-1" /> Esci
          </Button>
        </div>
      </header>

      <main className="px-4 sm:px-6 lg:px-8 py-6 max-w-6xl w-full mx-auto space-y-6">
        {/* Stats — il totale fatturato abbonamenti andrà qui in futuro (pagina dedicata) */}
        <div className="grid grid-cols-3 gap-3">
          <Stat icon={Store} label="Attività totali" value={stats?.businesses ?? "—"} tone="primary" />
          <Stat icon={CalendarCheck} label="Attive" value={stats?.active ?? "—"} tone="green" />
          <Stat icon={Pause} label="Sospese" value={stats?.suspended ?? "—"} tone="amber" />
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="relative w-full sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca attività o email…" className="pl-9" />
          </div>
          <CreateBusinessDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={load} />
        </div>

        {/* Lista attività */}
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 text-xs font-semibold text-slate-500 border-b border-slate-100">
            <div className="col-span-4">Attività</div>
            <div className="col-span-3">Titolare</div>
            <div className="col-span-1 text-center">Stato</div>
            <div className="col-span-2 text-center">Prenot. / Lead</div>
            <div className="col-span-2 text-right">Azioni</div>
          </div>

          {loading ? (
            <div className="p-8 text-center text-slate-400">Caricamento…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-400">Nessuna attività.</div>
          ) : filtered.map((b) => (
            <div key={b.id} data-testid="admin-business-row" className="grid grid-cols-1 md:grid-cols-12 gap-2 px-4 py-3 border-b border-slate-50 items-center hover:bg-slate-50/60">
              <div className="md:col-span-4">
                <p className="font-semibold text-slate-900">{b.name}</p>
                <p className="text-xs text-slate-500 capitalize">{b.type} · {b.mode === "nights" ? "notti" : "slot"} · creata {fmtDate(b.created_at)}</p>
              </div>
              <div className="md:col-span-3 text-sm text-slate-600 truncate">
                {b.owner_email}
                <p className="text-xs text-slate-400">ultimo accesso {fmtDate(b.last_login)}</p>
              </div>
              <div className="md:col-span-1 md:text-center">
                <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${b.status === "suspended" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                  {b.status === "suspended" ? "Sospesa" : "Attiva"}
                </span>
              </div>
              <div className="md:col-span-2 md:text-center text-sm text-slate-600">{b.reservations} / {b.leads}</div>
              <div className="md:col-span-2 flex md:justify-end gap-2">
                <Button size="sm" variant="outline" className="rounded-lg" onClick={() => enterAs(b)} data-testid="admin-enter-as">
                  <LogIn className="h-4 w-4 mr-1" /> Entra
                </Button>
                <Button size="sm" variant={b.status === "suspended" ? "default" : "ghost"} className="rounded-lg" onClick={() => toggleStatus(b)} data-testid="admin-toggle-status">
                  {b.status === "suspended" ? <><Play className="h-4 w-4 mr-1" /> Riattiva</> : <><Pause className="h-4 w-4 mr-1" /> Sospendi</>}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

function CreateBusinessDialog({ open, onOpenChange, onCreated }) {
  const empty = { name: "", owner_email: "", owner_password: "", type: "ristorante", mode: "slot", phone: "", address: "" };
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/businesses", f);
      toast.success("Attività creata ✅");
      onOpenChange(false); setF(empty); onCreated();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="rounded-xl" data-testid="admin-new-business"><Plus className="h-4 w-4 mr-1" /> Nuova attività</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Nuova attività</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Nome attività</Label><Input data-testid="cb-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Trattoria del Mare" required /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipo</Label>
              <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={f.type} onChange={(e) => set("type", e.target.value)}>
                {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <Label>Modalità</Label>
              <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={f.mode} onChange={(e) => set("mode", e.target.value)}>
                <option value="slot">Slot (tavoli/coperti)</option>
                <option value="nights">Notti (camere)</option>
              </select>
            </div>
          </div>
          <div><Label>Email titolare</Label><Input data-testid="cb-email" type="email" value={f.owner_email} onChange={(e) => set("owner_email", e.target.value)} placeholder="titolare@email.it" required /></div>
          <div><Label>Password iniziale</Label><Input data-testid="cb-password" value={f.owner_password} onChange={(e) => set("owner_password", e.target.value)} placeholder="min. 6 caratteri" required /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Telefono</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+39…" /></div>
            <div><Label>Indirizzo</Label><Input value={f.address} onChange={(e) => set("address", e.target.value)} placeholder="Città" /></div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} className="rounded-xl w-full" data-testid="cb-submit">{saving ? "Creazione…" : "Crea attività"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
